import React from 'react';
import { faRotate } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/knowledge-flywheel.jpg
import knowledgeFlywheelImg from '../../../assets/images/blogs/knowledge-flywheel.jpg';

const KnowledgeFlywheel = () => (
    <BlogPost
        title="Context Graphs and Knowledge Flywheel for Agents"
        subtitle="A living blueprint of a business that changes at the speed it does."
        category="Architecture"
        categoryIcon={faRotate}
        color="#003366"
        date="February 2026"
        readTime="6 min read"
        image={knowledgeFlywheelImg}
        imageAlt="Knowledge Flywheel - populate, execute, capture, learn, encode, repeat"
        ctaHeading="Ready to see the flywheel turn?"
        ctaText="Book a discovery call and we'll show you how the Knowledge Flywheel captures 80% automatically with SMEs tuning the rest."
        seoDescription="How the Knowledge Flywheel operates in full agentic mode, capturing domain, explicit, and tacit knowledge through continuous execution and learning."
        seoKeywords="knowledge flywheel, context graphs, agentic AI, tacit knowledge, domain knowledge, AgentDOG, Nebulyx"
        slug="knowledge-flywheel"
    >
        <p>
            Context graphs are "so hot right now". Seeing (and participating in) loads
            of great discussions. 🔥
        </p>

        <p>
            From these, two operating modes for knowledge have surfaced:
        </p>

        <h3>Two Operating Modes</h3>

        <p>
            <strong>1️⃣ Traditional Knowledge Management:</strong> People in a room
            interviewing, reviewing, discussing, then creating structured knowledge
            artefacts (KGs etc).
        </p>

        <p>
            <strong>2️⃣ Dynamic Knowledge Assembly (Agentic mode):</strong> Agents
            executing from a core question (either human or machine initiated), dynamic
            chains of reasoning, assembling knowledge for each reasoning step, deciding
            on the next step and repeating.
        </p>

        <p>
            <strong>This is where context graphs fit.</strong>
        </p>

        <p>
            Dynamic Knowledge Assembly is a mouthful, hence the "Knowledge Flywheel" -
            a living blueprint of a business that changes at the speed it does.
        </p>

        <p>
            The key shifts are: design-time to runtime, managed to dynamically assembled. ⚡
        </p>

        <h3>Three Knowledge Layers</h3>

        <p>
            Understanding what type you're working with matters:
        </p>

        <p>
            <strong>🌐 Domain:</strong> Regulations, industry manuals, clinical guidelines.
            External knowledge that governs your field.
        </p>

        <p>
            <strong>📋 Explicit:</strong> Company policies, process flows, transactions.
            Internal knowledge that's written down or captured in systems.
        </p>

        <p>
            <strong>🧠 Tacit:</strong> Undocumented expertise. Overrides, edge cases,
            judgment calls. Knowledge that only surfaces when decisions are made.
        </p>

        <p>
            Traditional approaches try to capture all three upfront manually. The comments
            across various threads show how hard it is and how long it can take. ⏳
        </p>

        <h3>The Knowledge Flywheel</h3>

        <p>
            Our AgentDOG approach is different &amp; operates in full Agentic mode.
        </p>

        <p>
            I.e. we don't try to capture everything upfront - we let the flywheel turn.
        </p>

        <p>
            <strong>Populate → Execute → Capture → Learn → Encode → Execute...</strong>
        </p>

        <p>
            <strong>🔹 Populate:</strong> AI-native semantics extracts meaning from domain
            docs and explicit knowledge. 80% done automatically. SMEs verify &amp; tune
            the last 20%.
        </p>

        <p>
            For our Doctor Dog example 🩺, we fed in NICE clinical guidelines (41 pages).
            96% vocabulary coverage. 80% critical questions answered out of the box.
            SMEs then tune the 20%.
        </p>

        <p>
            <strong>🔹 Execute:</strong> Agent navigates the decision space. States,
            transitions, constraints as nodes and edges. Context graphs persisted as
            decision traces on completion.
        </p>

        <p>
            <strong>🔹 Capture:</strong> Tacit knowledge enters here. SME overrides a
            decision? Captured with full context: state, reasoning, constraints. Knowledge
            born through action. 👤
        </p>

        <p>
            Also AI Digital Twins can run scenarios → SMEs override real situations,
            with capture. Runtime not design-time thinking.
        </p>

        <p>
            <strong>🔹 Learn:</strong> Reinforcement from outcomes. Patterns emerge &amp;
            precedent builds. 📈
        </p>

        <p>
            <strong>🔹 Encode:</strong> Knowledge crystallises back with new states &amp;
            transitions.
        </p>

        <p>
            Flywheel gets smarter. ✨ Then it turns again.
        </p>

        <h3>Why This Works</h3>

        <p>
            Tacit knowledge is easier to capture at runtime than upfront.
        </p>

        <p>
            Full context available in the moment beats interviewing experts weeks later.
        </p>

        <p>
            Context Graphs govern/capture action. Learn, evolve from and through actions.
        </p>

        <p>
            The flywheel turns. The knowledge lives. 🎯
        </p>
    </BlogPost>
);

export default KnowledgeFlywheel;
